// Remote control for the viewer's YouTube iframe (FR-C29 — the Shorts-like controls:
// tap to pause, ±10 s, hold for 2×, a live progress line). Nothing is loaded for it: the
// embed is built with `enablejsapi=1&origin=` (reel-embed.ts) and this file speaks the
// player's postMessage protocol directly — the same messages YouTube's own IFrame API
// script sends, minus the script. Commands go only to PLAYER_ORIGIN; messages from any
// other origin are ignored. Pure enough to unit-test with a fake window.
import { PLAYER_ORIGIN } from './reel-embed';

export interface ReelPlayerState {
  /** Seconds into the reel and its length; 0/0 until the player reports. */
  time: number;
  duration: number;
  playing: boolean;
}

export interface ReelPlayer {
  play(): void;
  pause(): void;
  /** Jump by `seconds` (negative = back), clamped to the reel. */
  seekBy(seconds: number): void;
  /** Jump to a fraction (0–1) of the reel — the progress line's scrub. */
  seekTo(fraction: number): void;
  setRate(rate: 1 | 2): void;
  mute(): void;
  unmute(): void;
  dispose(): void;
}

// YouTube's player states; everything else (unstarted, buffering, cued, ended) is "not playing".
const PLAYING = 1;

export function attachReelPlayer(iframe: HTMLIFrameElement, onState: (s: ReelPlayerState) => void, win: Window = window): ReelPlayer {
  const id = `reel-${Math.random().toString(36).slice(2, 8)}`;
  let state: ReelPlayerState = { time: 0, duration: 0, playing: false };
  let ready = false;
  let hello: ReturnType<typeof setInterval> | null = null;
  // Commands sent before the player has booted would be dropped by YouTube (the frame is still
  // loading), so they wait here and go out in order on onReady. A scrub made before the reel's
  // length is known waits for the first infoDelivery that carries it.
  const queue: { func: string; args: unknown[] }[] = [];
  let pendingFraction: number | null = null;

  const post = (msg: Record<string, unknown>) => {
    try {
      iframe.contentWindow?.postMessage(JSON.stringify({ ...msg, id, channel: 'widget' }), PLAYER_ORIGIN);
    } catch {
      /* the frame is gone or not yet navigated — the next tick retries */
    }
  };
  const send = (func: string, args: unknown[]) => post({ event: 'command', func, args });
  const command = (func: string, args: unknown[] = []) => {
    if (ready) send(func, args);
    else queue.push({ func, args });
  };
  const emit = (patch: Partial<ReelPlayerState>) => {
    state = { ...state, ...patch };
    onState(state);
  };

  function seekSeconds(t: number) {
    const max = state.duration > 0 ? state.duration : Number.POSITIVE_INFINITY;
    const clamped = Math.max(0, Math.min(t, max));
    command('seekTo', [clamped, true]);
    emit({ time: clamped });
  }

  const onMessage = (e: MessageEvent) => {
    if (e.origin !== PLAYER_ORIGIN || e.source !== iframe.contentWindow || typeof e.data !== 'string') return;
    let data: { event?: string; info?: { currentTime?: number; duration?: number; playerState?: number } };
    try {
      data = JSON.parse(e.data) as typeof data;
    } catch {
      return;
    }
    if (data.event === 'onReady') {
      ready = true;
      if (hello) clearInterval(hello);
      hello = null;
      // Ask for the time/state stream (what the IFrame API subscribes to behind the scenes).
      post({ event: 'listening' });
      queue.splice(0).forEach((c) => send(c.func, c.args));
      return;
    }
    if (data.event === 'infoDelivery' && data.info) {
      const { currentTime, duration, playerState } = data.info;
      emit({
        ...(typeof currentTime === 'number' ? { time: currentTime } : {}),
        ...(typeof duration === 'number' && duration > 0 ? { duration } : {}),
        ...(typeof playerState === 'number' ? { playing: playerState === PLAYING } : {}),
      });
      if (pendingFraction !== null && state.duration > 0) {
        const f = pendingFraction;
        pendingFraction = null;
        seekSeconds(f * state.duration);
      }
    }
  };
  win.addEventListener('message', onMessage);
  // The player answers "listening" with onReady once it has booted; until then, knock.
  hello = setInterval(() => {
    if (!ready) post({ event: 'listening' });
  }, 250);
  post({ event: 'listening' });


  return {
    play: () => command('playVideo'),
    pause: () => command('pauseVideo'),
    seekBy: (seconds) => seekSeconds(state.time + seconds),
    seekTo: (fraction) => {
      const f = Math.max(0, Math.min(1, fraction));
      if (state.duration > 0) seekSeconds(f * state.duration);
      else pendingFraction = f;
    },
    setRate: (rate) => command('setPlaybackRate', [rate]),
    mute: () => command('mute'),
    unmute: () => command('unMute'),
    dispose: () => {
      if (hello) clearInterval(hello);
      hello = null;
      win.removeEventListener('message', onMessage);
    },
  };
}
